import { AccountViewListingGroupType } from '@zg-rentals/ts-rental-manager-api-client';

import { getPhotoUrls } from './propertiesPageUtils';
import propertyUtils from './propertyUtils';
import stringHelper from './stringHelper';

import type {
  AccountViewListingGroupDto,
  AccountViewUnitCountsDto,
  RentalManagerAddressDto,
} from '@zg-rentals/ts-rental-manager-api-client';

export const getPropertyTypeLabel = (listing: AccountViewListingGroupDto) =>
  propertyUtils.getPropertyTypeLabel({ type: listing.type, propertyType: listing.propertyType });

export const getAddressLines = (listing: AccountViewListingGroupDto) => {
  const { address, name } = listing;
  const streetLine = address.unit ? `${address.street} #${address.unit}` : address.street || name;
  const { formattedCityStateZip } = propertyUtils.formatAddress(address as unknown as Record<string, string>);

  return { streetLine, cityStateZip: formattedCityStateZip };
};

export const getFormattedAddress = (listing: AccountViewListingGroupDto) => {
  const { streetLine, cityStateZip } = getAddressLines(listing);

  return `${streetLine} ${cityStateZip}`.trim();
};

export const isMultiplexListing = (listing: AccountViewListingGroupDto) =>
  listing.type === AccountViewListingGroupType.MULTIPLEX;

export const getUnitLabel = (unitCount: AccountViewUnitCountsDto | undefined) =>
  unitCount?.total != null ? stringHelper.pluralize(unitCount.total, 'unit', 'units') : null;

export const getVacantCount = (unitCount: AccountViewUnitCountsDto | undefined) => unitCount?.vacant ?? null;

// Address lines for a single unit's address, with the unit number omitted from the street line
// (the units table shows the unit separately in its own column).
export const getUnitAddressLines = (address: RentalManagerAddressDto) => {
  const { formattedStreet, formattedCityStateZip } = propertyUtils.formatAddress({ ...address, unit: '' });

  return { streetLine: formattedStreet, cityStateZip: formattedCityStateZip };
};

export const getUnitSummary = (listing: AccountViewListingGroupDto & { unitCount?: AccountViewUnitCountsDto }) => {
  if (listing.isMultifamily === true) {
    return '—';
  }

  const { total, vacant } = listing.unitCount ?? {};
  const unitLabel = total == null ? '— units' : stringHelper.pluralize(total, 'unit', 'units');
  const vacantLabel = stringHelper.pluralize(vacant ?? 0, 'vacancy', 'vacancies');

  return `${unitLabel} • ${vacantLabel}`;
};

export const getPhotoThumbUrl = (photoKey: string | undefined) => {
  if (!photoKey) {
    return '';
  }

  const isRunningInProd =
    typeof window !== 'undefined'
      ? window.location.hostname.includes('zillow.com') || window.location.hostname.includes('hotpads.com')
      : false;

  return getPhotoUrls(isRunningInProd, photoKey).thumb;
};
